/**
 * A recommended application worked out from a soil test's results (owner, 2026-09-27: "We can have
 * recommended application based on test results?").
 *
 * The ratings are the Soil Health Card critical limits used across India: available N, P and K in
 * kg/ha, micronutrients in ppm (DTPA), organic carbon in %. What comes back is advice for the
 * officer to confirm against the laboratory's own recommendation, not a prescription - doses depend
 * on the crop and the state's package of practices.
 */
export interface SoilResults {
  ph?: number | null; ec?: number | null; oc?: number | null;
  n?: number | null; p?: number | null; k?: number | null;
  s?: number | null; zn?: number | null; fe?: number | null; mn?: number | null; cu?: number | null; b?: number | null;
}

const has = (value: number | null | undefined): value is number => value !== null && value !== undefined && !Number.isNaN(value);

export function soilAdvice(r: SoilResults): string[] {
  const out: string[] = [];
  if (has(r.ph)) {
    if (r.ph < 5.5) out.push(`pH ${r.ph}: acidic. Apply agricultural lime (about 2-4 t/ha, split over seasons) before other inputs.`);
    else if (r.ph > 8.5) out.push(`pH ${r.ph}: alkaline / sodic. Apply gypsum as the laboratory advises and add organic matter.`);
    else if (r.ph > 7.5) out.push(`pH ${r.ph}: slightly alkaline. Prefer acid-forming fertilizers (ammonium sulphate) and organic matter.`);
  }
  if (has(r.ec)) {
    if (r.ec > 4) out.push(`EC ${r.ec} dS/m: saline. Improve drainage and leach salts before applying fertilizer.`);
    else if (r.ec > 1) out.push(`EC ${r.ec} dS/m: slightly saline. Avoid chloride-based fertilizers (use SOP instead of MOP).`);
  }
  if (has(r.oc)) {
    if (r.oc < 0.5) out.push(`Organic carbon ${r.oc}%: low. Apply FYM or compost 10-15 t/ha, or vermicompost 2.5-5 t/ha.`);
    else if (r.oc <= 0.75) out.push(`Organic carbon ${r.oc}%: medium. Apply FYM or compost 5-10 t/ha to maintain it.`);
  }
  if (has(r.n)) {
    if (r.n < 280) out.push(`Nitrogen ${r.n} kg/ha: low. Apply the recommended N dose plus 25% (e.g. urea), in splits.`);
    else if (r.n > 560) out.push(`Nitrogen ${r.n} kg/ha: high. Reduce the recommended N dose by 25%.`);
    else out.push(`Nitrogen ${r.n} kg/ha: medium. Apply the recommended N dose.`);
  }
  if (has(r.p)) {
    if (r.p < 10) out.push(`Phosphorus ${r.p} kg/ha: low. Apply the recommended P dose plus 25% (DAP or SSP) as basal.`);
    else if (r.p > 25) out.push(`Phosphorus ${r.p} kg/ha: high. Reduce the recommended P dose by 25%.`);
    else out.push(`Phosphorus ${r.p} kg/ha: medium. Apply the recommended P dose.`);
  }
  if (has(r.k)) {
    if (r.k < 108) out.push(`Potassium ${r.k} kg/ha: low. Apply the recommended K dose plus 25% (MOP).`);
    else if (r.k > 280) out.push(`Potassium ${r.k} kg/ha: high. Reduce the recommended K dose by 25%.`);
    else out.push(`Potassium ${r.k} kg/ha: medium. Apply the recommended K dose.`);
  }
  if (has(r.s) && r.s < 10) out.push(`Sulphur ${r.s} ppm: deficient. Apply 20-40 kg S/ha (gypsum or bentonite sulphur).`);
  if (has(r.zn) && r.zn < 0.6) out.push(`Zinc ${r.zn} ppm: deficient. Apply zinc sulphate 25 kg/ha once in two to three years.`);
  if (has(r.fe) && r.fe < 4.5) out.push(`Iron ${r.fe} ppm: deficient. Foliar spray of 0.5% ferrous sulphate.`);
  if (has(r.mn) && r.mn < 2) out.push(`Manganese ${r.mn} ppm: deficient. Foliar spray of 0.5% manganese sulphate.`);
  if (has(r.cu) && r.cu < 0.2) out.push(`Copper ${r.cu} ppm: deficient. Apply copper sulphate 5-10 kg/ha.`);
  if (has(r.b) && r.b < 0.5) out.push(`Boron ${r.b} ppm: deficient. Apply borax 10 kg/ha, or a 0.2% borax foliar spray.`);
  return out;
}
